import { action, computed, makeObservable, observable } from 'mobx'

import { IDeletedUserInfo, InviteStatus, IUser } from '~/client/graph'
import {
  IRemovePhoneNumberMutation,
  RemovePhoneNumberDocument,
} from '~/client/graph/operations/generated/PhoneTransfer.generated'
import { SendInvitesDocument } from '~/client/graph/operations/generated/Users.generated'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import User from '../../models/User'
import UserProject from '../../models/UserProject'
import { IServiceUserDto } from '../../types/UserDto'
import Guard from '../../utils/Guard'
import { mapFiltered } from '../../utils/util'
import EventsStore from '../EventStore/Events.store'
import GraphExecutorStore from './GraphExecutor.store'
import UserProjectsStore from './UserProjects.store'

export type PhoneClearOutcome =
  | { outcome: 'done' }
  | { outcome: 'confirm-strand'; message: string }
  | { outcome: 'error'; message: string }

const PHONE_REMOVE_ERROR = 'Could not remove the phone number.'
const PHONE_STRAND_WARNING = 'This user may lose the ability to sign in.'

export default class ProjectMembersStore {
  @observable private sourceMap: Map<string, User> = new Map()

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly userProjectsStore: UserProjectsStore,
    private readonly graphExecutorStore: GraphExecutorStore,
  ) {
    Guard.requireAll({ eventsStore, userProjectsStore, graphExecutorStore })

    this.setOne(User.service)
    makeObservable(this)
  }

  public getByIds = (userIds: string[] = []): User[] => {
    return mapFiltered(userIds, id => this.getById(id))
  }

  public getById = (userId: string): User => {
    return this.sourceMap.get(userId)
  }

  public getByEmail = (email: string): User => {
    return email ? this.list.find(m => m.email === email) : null
  }

  public getCompanyMembers = (companyId: string): User[] => {
    if (!companyId) {
      return []
    }

    return this.list.filter(
      member =>
        UserProject.getCompanyId(member, this.userProjectsStore) === companyId,
    )
  }

  public hasById = (memberId: string): boolean => {
    return this.sourceMap.has(memberId)
  }

  public hasUserAccessToActiveProjectById = (memberId: string): boolean => {
    return this.userProjectsStore.hasUserAccessToActiveProject(
      this.getById(memberId),
    )
  }

  public hasUserAccessToActiveProject = (member: User): boolean => {
    return this.userProjectsStore.hasUserAccessToActiveProject(member)
  }

  @computed
  public get list(): User[] {
    return [...this.sourceMap.values()]
      .filter(u => this.hasUserAccessToActiveProject(u))
      .sort((a, b) =>
        User.getFullName(a)
          .toLowerCase()
          .localeCompare(User.getFullName(b).toLowerCase()),
      )
  }

  @computed
  public get limitedList(): User[] {
    return this.list.filter(m =>
      UserProject.isAccepted(m, this.userProjectsStore),
    )
  }

  @action.bound
  public receiveList(dtos: IUser[]) {
    this.sourceMap.clear()
    this.setMany([...dtos, User.service])
  }

  private deteledUserToUser(dto: IDeletedUserInfo): User {
    return User.fromDto({
      ...dto,
    })
  }

  @action.bound
  public receiveDeletedList(dtos: IDeletedUserInfo[]) {
    dtos.forEach(dto => {
      this.sourceMap.set(dto.id, this.deteledUserToUser(dto))
    })
  }

  @action.bound
  public async saveMembers(
    members: IServiceUserDto[],
    reassignClaimedPhone: boolean = false,
  ) {
    const memberDtos = members.map(m => ({
      ...User.toDto(m),
      userProjectSettings: m.userProjectSettings,
    }))

    return new Promise((resolve, reject) =>
      this.eventsStore.dispatch(
        e.SAVE_MEMBERS,
        memberDtos,
        reassignClaimedPhone,
        resolve,
        reject,
      ),
    )
  }

  // STX1-637: single choke-point for the three phone-edit forms. Clearing a
  // number in a phone-edit form removes it everywhere (central + Firebase + all
  // tenants) via the auth-backed removePhoneNumber mutation, unlike saveMembers
  // where a blank preserves the existing number. Caller decides how to surface
  // the outcome (success toast / strand confirm / error alert).
  @action.bound
  public async removeMemberPhone(
    userId: string,
    confirmStrandSource = false,
  ): Promise<PhoneClearOutcome> {
    const res = await this.graphExecutorStore.mutate(
      RemovePhoneNumberDocument,
      {
        projectId: this.appState.activeProject.id,
        userId,
        confirmStrandSource,
      },
    )

    // mutate already dispatches REPORT_ERROR for transport/GraphQL failures.
    if (res.error) {
      return { outcome: 'error', message: PHONE_REMOVE_ERROR }
    }

    const result = (res.data as unknown as IRemovePhoneNumberMutation)
      ?.removePhoneNumber
    if (!result) {
      return { outcome: 'error', message: PHONE_REMOVE_ERROR }
    }

    if (result.success) {
      this.nullMemberPhone(userId)
      return { outcome: 'done' }
    }

    // Idempotency guard: a stale re-submit for a source that already lost its
    // number is a benign no-op, not an error.
    if (result.status === 'SourceHasNoNumber') {
      return { outcome: 'done' }
    }

    if (result.requiresConfirmation) {
      return {
        outcome: 'confirm-strand',
        message: result.message || PHONE_STRAND_WARNING,
      }
    }

    return { outcome: 'error', message: result.message || PHONE_REMOVE_ERROR }
  }

  // Mirror PhoneActions.setUserPhone: in-place cache update so the directory row
  // reflects the removal without a full reload. toDto drops isSignUpCreated and
  // stxAccountId, so carry them through.
  @action.bound
  private nullMemberPhone(userId: string) {
    const member = this.getById(userId)
    if (!member) {
      return
    }

    this.setOne({
      ...User.toDto(member),
      phoneNumber: null,
      isSignUpCreated: member.isSignUpCreated,
      stxAccountId: member.stxAccountId,
    })
  }

  public async deleteMembers(members: User[]) {
    const userProjectDtos = members.map(m => {
      const userProject = this.userProjectsStore
        .getByUser(m)
        .getCopy() as UserProject

      userProject.setSoftDeletion()
      return userProject.toDto()
    })

    await this.userProjectsStore.save(userProjectDtos)
  }

  @action.bound
  public async inviteMembers(dtos: IUser[]): Promise<boolean> {
    if (!dtos?.length) return

    const { loading, activeProject } = this.appState

    loading.set(e.INVITE_PROJECT_MEMBERS, true)

    const res = await this.graphExecutorStore.mutate(SendInvitesDocument, {
      projectId: activeProject.id,
      userData: dtos.map(({ email, phoneNumber }) => ({ email, phoneNumber })),
    })

    loading.set(e.INVITE_PROJECT_MEMBERS, false)

    if (res.error) {
      throw res.error
    }

    return res.data.sendInvites
  }

  @action.bound
  public async approveProjectMembers(members: IUser[]): Promise<boolean> {
    if (!members?.length) return

    const userProjectDtos = members.map(m => {
      const userProject = this.userProjectsStore
        .getByUser(m)
        .getCopy() as UserProject

      userProject.setInviteStatus(InviteStatus.Pending)

      return userProject.toDto()
    })

    this.appState.loading.set(e.APPROVE_PROJECT_MEMBERS, true)

    try {
      await this.userProjectsStore.save(userProjectDtos)
    } finally {
      this.appState.loading.set(e.APPROVE_PROJECT_MEMBERS, false)
      this.inviteMembers(members)
    }
  }

  @action.bound
  public async resetInvitation(members: IUser[]): Promise<boolean> {
    if (!members?.length) return

    const userProjectDtos = members.map(m => {
      const userProject = this.userProjectsStore
        .getByUser(m)
        .getCopy() as UserProject

      userProject.resetInvitation()
      return userProject.toDto()
    })

    this.appState.loading.set(e.SET_PROJECT_MEMBERS_AS_NOT_INVITED, true)

    try {
      await this.userProjectsStore.save(userProjectDtos)
    } finally {
      this.appState.loading.set(e.SET_PROJECT_MEMBERS_AS_NOT_INVITED, false)
    }
  }

  @action.bound
  public setOne(dto: IUser) {
    this.sourceMap.set(dto.id, User.fromDto(dto))
  }

  @action.bound
  private setMany(dtos: IUser[]) {
    dtos.forEach(this.setOne)
  }

  private get appState() {
    return this.eventsStore.appState
  }
}
